/**
 * PrintTemplatesAdmin.tsx — Admin > Forms & Templates > Print Templates.
 *
 * The corporate dashboard's Print Templates page for the signed-in account:
 * the layouts of the printed quote, receipt and membership agreement
 * (`quote_print_templates`), in the Admin list layout:
 *   - Name, Offered For and Disclosure, by name; search by name or disclosure,
 *     filter by what the template is offered for, Show deleted;
 *   - "Add Template" top right; a click on a row opens the builder
 *     (PrintTemplateBuilder); Edit / Delete on a live template, Restore on a
 *     deleted one.
 * The old page here edited the unrelated `print_templates` table.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add, row icons or Actions column; a template still opens in
 * the builder, read-only (no drag, no Save; Preview stays).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { ListFilterBar } from '@/components/admin/ListFilterBar';
import { PrintTemplateBuilder } from '@/components/admin/PrintTemplateBuilder';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { getErrorMessage } from '@/services/apiClient';
import {
  deletePrintTemplate,
  listPrintDisclosures,
  listPrintTemplates,
  offeredForLabel,
  restorePrintTemplate,
  type PrintDisclosure,
  type PrintTemplate,
} from '@/services/printTemplatesAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', className: 'w-[32%]', sortable: true },
  { key: 'offeredFor', header: 'Offered For', className: 'w-[20%]' },
  { key: 'disclosure', header: 'Disclosure' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

const OFFERED_FOR_OPTIONS = [
  { value: 'forQuotes', label: 'Quotes' },
  { value: 'forReceipts', label: 'Receipts' },
  { value: 'forMemberships', label: 'Memberships' },
];

type OfferedKey = 'forQuotes' | 'forReceipts' | 'forMemberships';

function templateName(t: PrintTemplate): string {
  return t.name.trim() || `Template #${t.quotePrintTemplateID}`;
}

const SORT: ListSortCompare<PrintTemplate> = {
  name: (a, b) => compareText(templateName(a), templateName(b)),
};

function DisclosureCell({ template }: { template: PrintTemplate }) {
  if (!template.flags.disclosures) return <span className="text-slate-400">Hidden</span>;
  if (!template.disclosureName) return <span className="text-amber-600">Displayed, none selected</span>;
  return (
    <span className="block truncate" title={template.disclosureName}>
      {template.disclosureName}
    </span>
  );
}

export function PrintTemplatesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const [disclosures, setDisclosures] = useState<PrintDisclosure[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [offeredFor, setOfferedFor] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<PrintTemplate | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PrintTemplate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, discl] = await Promise.all([listPrintTemplates(), listPrintDisclosures().catch(() => [] as PrintDisclosure[])]);
      setTemplates(list);
      setDisclosures(discl);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load print templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const filtering = query !== '' || offeredFor.length > 0;
  const rows = useMemo(
    () =>
      templates.filter(
        (t) =>
          (showDeleted || t.active) &&
          (query === '' || t.name.toLowerCase().includes(query) || (t.disclosureName ?? '').toLowerCase().includes(query)) &&
          // A template matches when it is offered for ANY of the picked documents.
          (offeredFor.length === 0 || offeredFor.some((k) => t.flags[k as OfferedKey])),
      ),
    [templates, showDeleted, query, offeredFor],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, offeredFor, showDeleted] });

  function handleSaved(saved: PrintTemplate, isNew: boolean) {
    setTemplates((prev) =>
      isNew ? [...prev, saved] : prev.map((t) => (t.quotePrintTemplateID === saved.quotePrintTemplateID ? saved : t)),
    );
  }

  async function handleDelete() {
    if (!deleting) return;
    const id = deleting.quotePrintTemplateID;
    try {
      await deletePrintTemplate(id);
      setTemplates((prev) => prev.map((t) => (t.quotePrintTemplateID === id ? { ...t, active: false } : t)));
      toast.success('Print template deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Delete failed'));
    }
  }

  async function handleRestore(template: PrintTemplate) {
    const id = template.quotePrintTemplateID;
    try {
      await restorePrintTemplate(id);
      setTemplates((prev) => prev.map((t) => (t.quotePrintTemplateID === id ? { ...t, active: true } : t)));
      toast.success('Print template restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Restore failed'));
    }
  }

  return (
    <AdminListPage
      title="Print Templates"
      subtitle="Layouts for printable quotes, receipts and membership agreements — which lines print, in what order, and which disclosure is appended."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add Template
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or disclosure..."
          searchLabel="Search print templates"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="print-templates-show-deleted"
        >
          <ListFilterBar
            filters={[
              {
                id: 'print-templates-offered-filter',
                label: 'Filter by offered for',
                placeholder: 'Offered For',
                searchPlaceholder: 'Filter...',
                emptyMessage: 'No options.',
                options: OFFERED_FOR_OPTIONS,
                selected: offeredFor,
                onChange: setOfferedFor,
              },
            ]}
          />
        </AdminListToolbar>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading print templates…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={filtering ? 'No print templates found' : 'No print templates yet'}
          hint={
            filtering ? 'Try adjusting your search or filters.' : locked ? undefined : 'Add a template to choose what your printed quotes and receipts show.'
          }
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Print templates" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((t) => {
              const deleted = !t.active;
              const name = templateName(t);
              return (
                <tr
                  key={t.quotePrintTemplateID}
                  data-template-id={t.quotePrintTemplateID}
                  className={adminRowClass({ deleted, clickable: true })}
                  onClick={() => setEditing(t)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="print-template-name">
                      {name}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>{offeredForLabel(t.flags)}</td>
                  <td className={ADMIN_TD_CLASS}>
                    <DisclosureCell template={t} />
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(t)} />
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(t)} />
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['template', 'templates']} />
        </>
      )}

      {editing && (
        <PrintTemplateBuilder
          key={editing === 'new' ? 'new' : editing.quotePrintTemplateID}
          template={editing === 'new' ? null : editing}
          templates={templates}
          disclosures={disclosures}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${templateName(deleting)}"?` : ''}
        message="Receipts already generated from it are unaffected. You can restore it from Show deleted."
        confirmLabel="Delete template"
        variant="danger"
      />
    </AdminListPage>
  );
}
